import { Refresh } from "@mui/icons-material";
import { ClickAwayListener, Fade, Paper, Popper } from "@mui/material";
import { FC, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  AccessControl,
  RebuildRouteDropdownAppliedInfo,
  RebuildRouteDropdownCustomRebuildHeading,
  RebuildRouteDropdownMenu,
  RebuildRouteDropdownSubmitButton,
  RebuildRouteProviderSelector,
} from "@ryde/controls";
import {
  CourseFormFields,
  CourseRouteTypeEnum,
  formatRebuildRouteNoCacheButtonCopy,
  formatRebuildRouteTooltip,
  ICourseDto,
  RebuildRouteTypeEnum,
  resolveRebuildRouteProvider,
} from "@ryde/shared";

import { CourseRebuildRouteDropdownWrapper } from "./course-rebuild-route-dropdown.styled";
import {
  CourseRebuildRouteDropdownDefaultAction,
  CourseRebuildRouteDropdownOptionsSelector,
  CourseRebuildRouteDropdownTopButton,
} from "./parts";
import { useCourseRebuildRouteDropdown } from "./use-course-rebuild-route-dropdown";

interface ICourseRebuildRouteDropdownProps {
  courseDto: Partial<ICourseDto> | undefined;
}

export const CourseRebuildRouteDropdown: FC<
  ICourseRebuildRouteDropdownProps
> = ({ courseDto }) => {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { watch, setValue } = useFormContext<Partial<ICourseDto>>();
  const isManualPolylineEditFromForm = watch(
    CourseFormFields.isManualPolylineEdit,
  );
  const isShuttleLikeCourse =
    courseDto?.routeType === CourseRouteTypeEnum.Shuttle ||
    courseDto?.routeType === CourseRouteTypeEnum.Flex;
  const showStationsReorderOptions = !isShuttleLikeCourse;

  const {
    open,
    isLoading,
    systemDefaultProvider,
    lastUsedProvider,
    selectedProvider,
    setSelectedProvider,
    avoidTolls,
    setAvoidTolls,
    skipCache,
    setSkipCache,
    hasUserOwnedRoutingWaypoints,
    routeOption,
    setRouteOption,
    supportsAvoidTolls,
    systemDefaultAvoidTolls,
    handleDropdownToggle,
    handleClose: handleCloseFromHook,
    handleDefaultRebuild,
    handleRebuild,
    lastSelectedRebuildType,
    lastRouteRebuildAt,
    hasCustomRebuildType,
  } = useCourseRebuildRouteDropdown(
    courseDto,
    isManualPolylineEditFromForm,
    routePolyline => {
      // keep form state in sync, otherwise PolylineEdit keeps rendering old manual polyline
      setValue(CourseFormFields.routePolyline, routePolyline, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(CourseFormFields.isManualPolylineEdit, false, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
  );

  const rebuildRouteTooltip = useMemo(
    () =>
      formatRebuildRouteTooltip({
        baseLabel: t("general.REBUILD_SAVE_CACHE"),
        lastUsedProvider,
        systemDefaultProvider,
      }),
    [lastUsedProvider, systemDefaultProvider, t],
  );

  const rerouteProvider = resolveRebuildRouteProvider({
    selectedProvider,
    lastUsedProvider,
    systemDefaultProvider,
  });
  const rerouteRebuildType =
    routeOption || RebuildRouteTypeEnum.ProviderOptimization;

  const noCacheButtonCopy = useMemo(
    () =>
      formatRebuildRouteNoCacheButtonCopy({
        provider: rerouteProvider,
        rebuildType: rerouteRebuildType,
        t,
      }),
    [rerouteProvider, rerouteRebuildType, t],
  );

  const handleClose = (event: Event | React.SyntheticEvent): void => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    handleCloseFromHook(event);
  };

  return (
    <AccessControl entity="course" action="update">
      <CourseRebuildRouteDropdownWrapper $isOpen={open}>
        <CourseRebuildRouteDropdownTopButton
          anchorRef={anchorRef}
          isLoading={isLoading}
          hasCustomRebuildType={hasCustomRebuildType}
          rebuildRouteTooltip={rebuildRouteTooltip}
          onDefaultRebuild={() => {
            void handleDefaultRebuild();
          }}
          onDropdownToggle={handleDropdownToggle}
        />

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          transition
          disablePortal
          placement="bottom-end"
          style={{ zIndex: 1300 }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={350}>
              <Paper elevation={8}>
                <ClickAwayListener onClickAway={handleClose}>
                  <RebuildRouteDropdownMenu>
                    <RebuildRouteDropdownAppliedInfo
                      lastUsedProvider={lastUsedProvider}
                      systemDefaultProvider={systemDefaultProvider}
                      sessionRouteRebuildAt={lastRouteRebuildAt}
                      lastSelectedRebuildType={lastSelectedRebuildType}
                      onClose={handleClose}
                    />

                    <CourseRebuildRouteDropdownDefaultAction
                      skipCache={skipCache}
                      isSkipCacheDisabled={hasUserOwnedRoutingWaypoints}
                      hasUserOwnedRoutingWaypoints={
                        hasUserOwnedRoutingWaypoints
                      }
                      isLoading={isLoading}
                      lastUsedProvider={lastUsedProvider}
                      systemDefaultProvider={systemDefaultProvider}
                      onSkipCacheChange={setSkipCache}
                      onRebuild={handleDefaultRebuild}
                    />

                    <RebuildRouteDropdownCustomRebuildHeading />

                    <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--providers">
                      <RebuildRouteProviderSelector
                        selectedProvider={selectedProvider}
                        systemDefaultProvider={systemDefaultProvider}
                        lastUsedProvider={lastUsedProvider}
                        onProviderChange={setSelectedProvider}
                      />
                    </div>

                    <div className="rebuild-route-dropdown-plate rebuild-route-dropdown-plate--options">
                      <CourseRebuildRouteDropdownOptionsSelector
                        supportsAvoidTolls={supportsAvoidTolls}
                        avoidTolls={avoidTolls}
                        isDefaultAvoidTolls={systemDefaultAvoidTolls}
                        routeOption={routeOption}
                        selectedProvider={rerouteProvider}
                        showStationsReorderOptions={showStationsReorderOptions}
                        onAvoidTollsChange={setAvoidTolls}
                        onRouteOptionChange={setRouteOption}
                      />

                      <RebuildRouteDropdownSubmitButton
                        label={noCacheButtonCopy.title}
                        hint={noCacheButtonCopy.hint}
                        tooltip={t("general.RE_ROUTE_NO_CACHE_TOOLTIP")}
                        startIcon={<Refresh />}
                        isLoading={isLoading}
                        onClick={handleRebuild}
                      />
                    </div>
                  </RebuildRouteDropdownMenu>
                </ClickAwayListener>
              </Paper>
            </Fade>
          )}
        </Popper>
      </CourseRebuildRouteDropdownWrapper>
    </AccessControl>
  );
};
